import { createContext, useContext, useRef, useState, type ReactNode } from 'react';
import { FinalidadeEstado, type ProcessoCartaoDto } from '@nexital/shared';
import { useQuadro } from '../../application/dependencias';
import type { PedidoMudanca } from '../../application/quadro-store';
import type { Ponto } from '../../domain/camera';
import { DialogoEstado, type PerguntaEstado } from './DialogosEstado';

/** Ações de mudança de estado com as confirmações da tela (RQ-EST-006 a RQ-EST-011). */
export type AcoesEstado = {
  /** Muda o estado; havendo avisos, pergunta (com motivo) e reenvia. `voltar` anima o cartão de volta se desistir. */
  mudar(pedido: PedidoMudanca, voltar?: () => void): Promise<boolean>;
  /** Cartão solto fora de qualquer grupo: confirma e leva para Sem estado (posição absoluta no quadro). */
  enviarSemEstado(processo: ProcessoCartaoDto, posicao: Ponto, voltar: () => void): Promise<boolean>;
  /** Confirma (motivo opcional) e leva para a Lixeira. */
  enviarLixeira(processo: ProcessoCartaoDto, voltar?: () => void): Promise<boolean>;
  /** Pede o motivo da lista do Arquivo e arquiva. */
  arquivar(processo: ProcessoCartaoDto): Promise<boolean>;
};

const Contexto = createContext<AcoesEstado | null>(null);

export function useAcoesEstado() {
  const acoes = useContext(Contexto);
  if (!acoes) throw new Error('ProvedorAcoesEstado ausente');
  return acoes;
}

const nada = () => {};

export function ProvedorAcoesEstado({ children }: { children: ReactNode }) {
  const dados = useQuadro((s) => s.dados);
  const mudarEstado = useQuadro((s) => s.mudarEstado);
  type Aberta = PerguntaEstado & { chave: number; responder: (valor: string | null) => void };
  const [pergunta, definirPergunta] = useState<Aberta | null>(null);
  const contador = useRef(0);

  /** Abre a caixa e espera a resposta: o valor digitado ou escolhido, ou null se a pessoa cancelar. */
  const perguntar = (p: PerguntaEstado) =>
    new Promise<string | null>((resolver) => {
      const responder = (valor: string | null) => {
        definirPergunta(null);
        resolver(valor);
      };
      contador.current += 1;
      definirPergunta({ ...p, chave: contador.current, responder });
    });
  const desistir = (voltar: () => void) => {
    voltar();
    return false;
  };
  const estadoDe = (finalidade: FinalidadeEstado) => dados?.estados.find((e) => e.finalidade === finalidade);

  const mudar: AcoesEstado['mudar'] = async (pedido, voltar = nada) => {
    const resultado = await mudarEstado(pedido);
    if (resultado.tipo !== 'avisos') return resultado.tipo === 'gravado';
    const motivo = await perguntar({ tipo: 'avisos', avisos: resultado.avisos });
    if (motivo !== null) return mudar({ ...pedido, confirmarAvisos: true, motivoTexto: motivo }, voltar);
    // Primeiro marca o cartão para voltar animado; depois desfaz a mudança otimista.
    const desistiu = desistir(voltar);
    resultado.desfazer();
    return desistiu;
  };

  const acoes: AcoesEstado = {
    mudar,
    enviarSemEstado: async (processo, posicao, voltar) => {
      const semEstado = estadoDe(FinalidadeEstado.SemEstado);
      if (!semEstado || (await perguntar({ tipo: 'sem-estado', processo })) === null) return desistir(voltar);
      return mudar({ processoUuid: processo.uuid, estadoUuid: semEstado.uuid, posicao }, voltar);
    },
    enviarLixeira: async (processo, voltar = nada) => {
      const lixeira = estadoDe(FinalidadeEstado.Lixeira);
      const motivo = lixeira ? await perguntar({ tipo: 'lixeira', processo }) : null;
      if (!lixeira || motivo === null) return desistir(voltar);
      const pedido = { processoUuid: processo.uuid, estadoUuid: lixeira.uuid, posicao: { x: processo.x, y: processo.y } };
      return mudar({ ...pedido, motivoTexto: motivo || undefined }, voltar);
    },
    arquivar: async (processo) => {
      const arquivo = estadoDe(FinalidadeEstado.Arquivo);
      const motivoUuid = arquivo ? await perguntar({ tipo: 'arquivo', processo, motivos: arquivo.motivos }) : null;
      if (!arquivo || !motivoUuid) return false;
      return mudar({ processoUuid: processo.uuid, estadoUuid: arquivo.uuid, posicao: { x: processo.x, y: processo.y }, motivoUuid });
    },
  };

  return (
    <Contexto.Provider value={acoes}>
      {children}
      {pergunta && <DialogoEstado key={pergunta.chave} pergunta={pergunta} responder={pergunta.responder} />}
    </Contexto.Provider>
  );
}
